/**
 * "What I did": one-tap corrective actions for every finding (owner, 2026-10-01: "When something needs a fix: we should
 * have quick buttons for it that get it health code compliant for 'what I did', either for manager or employee who handles
 * it. If a fix is ordered…").
 *
 * A finding is a log row that broke one of its template's rules (templates.ts). For each rule, CATALOG lists what a person
 * may have done about it, worded in the first person, each with:
 *   - its source: the Food Code section (FDA Food Code 2022, adopted in Utah by R392-100), a Utah rule, the maker's label or
 *     manual, or "Our advice" when no rule says it (W1);
 *   - its outcome: `closes` (the finding is fixed), `recheck` (open until a reading after a set time is in range), or
 *     `pending` (waiting on a repair or a supply, with the interim control the Code asks for meanwhile) (W2);
 *   - who may pick it: anyone, or the person in charge (a manager, owner or the shift's person in charge). Staff who pick a
 *     person-in-charge option hand it to that person instead; it is never written as done (W3).
 *
 * | Gate | Rule |
 * |---|---|
 * | W1 Sourced | Every option, every interim control and every person-in-charge rule names its source; anything not in a rule is "Our advice" |
 * | W2 Honest status | The record says "Fixed" only when a closing option was picked (or a recheck came back in range). A repair called for is "Waiting on the repair"; a hand-off is "Handed to Maria"; a recheck is "Recheck due" |
 * | W3 Hand-off, not a wall | A person-in-charge option offered to staff becomes "Hand this to Maria", with what to do meanwhile (keep the food out of use) |
 * | W4 Recheck reminded | A recheck becomes an expected check on Today at its time (schedule.ts), so the usual due/late reminders reach the person by their reach preferences |
 * | W5 Ordered → arrived → recheck → fixed | A pending supply is on the order list until it arrives; a pending repair until someone taps "It's fixed"; either asks for the recheck reading, and an in-range recheck closes it |
 * | W6 No blame | The words say what was done, never who failed. Credit goes to the person who did it ("What I did: …, by Sam") |
 * | W7 No health words out | Hand-off details and notices name the thing ("walk-in fridge") and the action, never anything about anyone's health |
 *
 * Who may pick what (the person-in-charge column) is sourced where the Code assigns it, and an owner decision otherwise:
 *   - Stopping food work for an imminent health hazard: the permit holder (Food Code 8-404.11(A)) — person in charge.
 *   - Time as the control (3-501.19): needs written procedures prepared in advance and kept (3-501.19(A)(1); 2-103.11(Q)) —
 *     person in charge.
 *   - Calling for a repair, ordering a part or a supply: it spends the business's money — person in charge (our advice).
 *   - Throwing food out: the Code puts no person on it (3-701.11 says the food "shall be discarded"), so anyone may when the
 *     option's own condition is met. Safe in both directions: discarding never harms a diner.
 *
 * Pure functions: the API, the web demo and the phone demo record the same way.
 */
import type { CellValue, Evaluation, Id, IsoDateTime } from "./model.ts";
import { evaluateRow, normalizeRow } from "./rules.ts";
import { getTemplate } from "./templates.ts";
import type { TaskAssignment } from "./delegate.ts";
import { WHATIDID_UT } from "./states/ut/whatidid.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof WHATIDID_UT>> = perStateTable("WHATIDID", WHATIDID_UT);
/** The catalog below is built once, from the default state's words (Utah). A null slot leaves that state-rule source out. */
const W = wordsFor(WORDS);
const utRule = (cite: string | null): FixSource[] => (cite ? [ut(cite)] : []);

/* ─────────────── sources ─────────────── */

export type FixSourceKind = "food_code" | "utah" | "maker" | "advice";
export interface FixSource { kind: FixSourceKind; cite: string }
const fc = (cite: string): FixSource => ({ kind: "food_code", cite: `Food Code ${cite}` });
const ut = (cite: string): FixSource => ({ kind: "utah", cite });
const maker = (cite: string): FixSource => ({ kind: "maker", cite });
/** "Our advice", with where the advice comes from when there is a place (the protocols in docs/source). */
const advice = (why?: string): FixSource => ({ kind: "advice", cite: why ? `Our advice (${why})` : "Our advice" });

/** The short line shown under an option: "Food Code 3-501.16(A)(2)", "Our advice". */
export function sourceLine(sources: readonly FixSource[]): string {
  return sources.map((s) => (s.kind === "advice" ? "Our advice" : s.cite)).filter((v, i, a) => a.indexOf(v) === i).join(" · ");
}

/* ─────────────── options ─────────────── */

export type FixOutcome = "closes" | "recheck" | "pending";
export type FixWho = "anyone" | "pic";

export interface Interim { text: string; sources: FixSource[] }

export interface FixOption {
  /** Stable id, stored on the record. */
  id: string;
  /** First person, past tense, the action and its limit: "Moved the food to a working fridge (41°F or below)". */
  label: string;
  /** When this is the right choice, in plain words ("Warm under 2 hours"). */
  when?: string;
  sources: FixSource[];
  outcome: FixOutcome;
  /** `recheck`: minutes until the recheck reading is due. */
  recheckMin?: number;
  who: FixWho;
  /** Why only the person in charge (W1). */
  whoWhy?: FixSource;
  /**
   * Food over its temperature limit (W8): the "how long over the limit?" answers this option fits. Absent: any answer, or
   * no question (the option isn't about the food's time).
   */
  bands?: OverBand[];
  /** `pending`: what it waits on, and the control the Code asks for meanwhile. */
  pending?: { kind: "repair" | "supply"; supplyKey?: string; interim: Interim };
}

export interface FindingFix { ruleId: string; problem: string; options: FixOption[] }

/** The one "Something else" button: a person's own short words (W2: their words, closes, no source claimed). */
export const SOMETHING_ELSE = "Something else";
export const OTHER_MAX = 200;

const SPENDS: FixSource = advice("calling for a repair or ordering spends the business's money, so the person in charge decides");
const TIME_PLAN_WHO: FixSource = fc("3-501.19(A)(1), 2-103.11(Q): written time-control procedures, kept by the person in charge");
const STOP_WHO: FixSource = fc("8-404.11(A): the permit holder stops operations");

/** "Called for repair" for a piece of equipment, with what to do until it's fixed. */
function repair(id: string, label: string, interim: Interim, extra: FixSource[] = []): FixOption {
  return { id, label, sources: [fc("4-501.11"), ...extra], outcome: "pending", who: "pic", whoWhy: SPENDS, pending: { kind: "repair", interim } };
}
/** "Ordered more <supply>", on the order list until it comes. */
function order(id: string, label: string, supplyKey: string, interim: Interim, extra: FixSource[] = []): FixOption {
  return { id, label, sources: extra.length ? extra : [advice()], outcome: "pending", who: "pic", whoWhy: SPENDS, pending: { kind: "supply", supplyKey, interim } };
}

/* ── cold holding ── */

const KEEP_COLD: Interim = { text: "Keep TCS food in another unit at 41°F or below until it's fixed.", sources: [fc("3-501.16(A)(2)")] };
const WARM_DISCARD = (what = "the TCS food"): FixOption => ({
  id: "cold.discard", label: `Threw out ${what}`,
  when: "Above 41°F 2 hours or more, or nobody knows how long. At 4 hours it must go",
  sources: [fc("3-501.19(B)(4)-(5)"), fc("3-701.11(A)"), advice("2 to 4 hours: throw it out, parish protocol, corrective actions at CCPs")], outcome: "closes", who: "anyone",
});
const TIME_PLAN: FixOption = {
  id: "cold.time_plan", label: "Marked it to use or throw out within 4 hours of leaving 41°F (written time plan)",
  when: "Only where your place has a written time-control plan",
  sources: [fc("3-501.19(A)-(B)")], outcome: "closes", who: "pic", whoWhy: TIME_PLAN_WHO, bands: ["under2", "2to4"],
};

/** The unit's air reads warm (cold_units fridge): the food may still be cold. */
const FRIDGE: FixOption[] = [
  { id: "fridge.food_ok", label: "Checked the food temps: all at 41°F or below. Will recheck in 30 min", sources: [fc("3-501.16(A)(2)"), advice("recheck the unit in 30 minutes")], outcome: "recheck", recheckMin: 30, who: "anyone" },
  { id: "fridge.moved", label: "Moved the food to a working fridge (41°F or below)", when: "Warm under 2 hours", sources: [fc("3-501.16(A)(2)"), advice("under 2 hours warm: chill it right away, parish protocol, corrective actions at CCPs")], outcome: "closes", who: "anyone", bands: ["under2"] },
  WARM_DISCARD(),
  { id: "fridge.adjusted", label: "Adjusted the thermostat. Will recheck in 30 min", sources: [advice("recheck after an adjustment"), fc("4-501.11")], outcome: "recheck", recheckMin: 30, who: "anyone" },
  repair("fridge.repair", "Called for repair", KEEP_COLD),
  repair("fridge.part", "Ordered the part", KEEP_COLD),
];

/** The food itself reads warm (cold hold, TCS items, food on the road). */
const FOOD_COLD: FixOption[] = [
  { id: "cold.chilled", label: "Chilled it right away (ice bath or a working fridge). Will recheck in 30 min", when: "Warm under 2 hours", sources: [fc("3-501.16(A)(2)"), advice("under 2 hours warm: chill it right away, parish protocol, corrective actions at CCPs")], outcome: "recheck", recheckMin: 30, who: "anyone", bands: ["under2"] },
  WARM_DISCARD("it"),
  TIME_PLAN,
];

/** Freezers: the Food Code sets no freezer temperature; 0°F is our protocol. */
const FREEZER: FixOption[] = [
  { id: "freezer.frozen", label: "Checked the food: still frozen hard. Will recheck in 30 min", sources: [advice("0°F freezers, parish protocol, freezer organization")], outcome: "recheck", recheckMin: 30, who: "anyone" },
  { id: "freezer.moved", label: "Moved the food to a working freezer", sources: [advice("parish protocol, freezer organization")], outcome: "closes", who: "anyone" },
  { id: "freezer.thawed", label: "Moved thawed food to the fridge (41°F or below) to use first", sources: [fc("3-501.16(A)(2)"), fc("3-501.13")], outcome: "closes", who: "anyone" },
  repair("freezer.repair", "Called for repair", { text: "Keep frozen food in a working freezer, or thawed food in a fridge at 41°F or below.", sources: [fc("3-501.16(A)(2)"), advice()] }),
];

/* ── hot holding ── */

const KEEP_HOT: Interim = { text: "Hold hot food in another unit at 135°F or above until it's fixed.", sources: [fc("3-501.16(A)(1)")] };
const HOT = (limit: string): FixOption[] => [
  { id: "hot.reheated", label: "Reheated it to 165°F within 2 hours", when: `Below ${limit} under 2 hours`, sources: [fc("3-403.11(A), (D)"), advice("under 2 hours: reheat, parish protocol, corrective actions at CCPs; Food Code Annex 5 says it may most often be rapidly reheated")], outcome: "closes", who: "anyone", bands: ["under2"] },
  { id: "hot.discard", label: "Threw it out", when: `Below ${limit} 2 hours or more, or nobody knows how long. At 4 hours it must go`, sources: [fc("3-501.19(B)(4)-(5)"), fc("3-701.11(A)"), advice("2 to 4 hours: throw it out, parish protocol, corrective actions at CCPs")], outcome: "closes", who: "anyone" },
  { id: "hot.time_plan", label: `Marked it to use or throw out within 4 hours of leaving ${limit} (written time plan)`, when: "Only where your place has a written time-control plan", sources: [fc("3-501.19(A)-(B)")], outcome: "closes", who: "pic", whoWhy: TIME_PLAN_WHO, bands: ["under2", "2to4"] },
  repair("hot.repair", "Called for repair on the warmer", KEEP_HOT),
];

/* ── cooking and reheating ── */

const COOK = (limit: string): FixOption[] => [
  { id: "cook.kept", label: `Kept cooking until it reached ${limit}`, sources: [fc("3-401.11"), fc("Annex 4, Principle 5")], outcome: "closes", who: "anyone" },
  { id: "cook.discard", label: "Threw it out", when: "It can't reach the temperature", sources: [fc("3-701.11(A)")], outcome: "closes", who: "anyone" },
];
const REHEAT: FixOption[] = [
  { id: "reheat.kept", label: "Kept reheating: it reached 165°F within 2 hours", sources: [fc("3-403.11(A), (D)")], outcome: "closes", who: "anyone" },
  { id: "reheat.discard", label: "Threw it out: it didn't reach 165°F within 2 hours", sources: [fc("3-403.11(D)"), fc("3-701.11(A)")], outcome: "closes", who: "anyone" },
];

/* ── cooling ── */

const COOL_2H: FixOption[] = [
  { id: "cool.restart", label: "Reheated it to 165°F and started cooling again a faster way", when: "Within the first 2 hours of cooling", sources: [fc("Annex 5, 7 Assessing Cooling"), fc("3-501.15(A)")], outcome: "closes", who: "anyone" },
  { id: "cool.discard", label: "Threw it out", when: "Above 70°F more than 2 hours into cooling", sources: [fc("Annex 5, 7 Assessing Cooling"), fc("3-501.14(A)(1)"), fc("3-701.11(A)")], outcome: "closes", who: "anyone" },
];
const COOL_6H: FixOption[] = [
  { id: "cool.discard", label: "Threw it out", when: "Above 41°F at 6 hours", sources: [fc("Annex 5, 7 Assessing Cooling"), fc("3-501.14(A)(2)"), fc("3-701.11(A)")], outcome: "closes", who: "anyone" },
];

/* ── sanitizer ── */

const SANITIZER: FixOption[] = [
  { id: "san.remade", label: "Remade the sanitizer to label strength and retested", sources: [fc("4-501.114"), fc("7-204.11"), maker("the sanitizer's label")], outcome: "closes", who: "anyone" },
  order("san.order_strips", "Out of test strips: ordered more", "test-strips", { text: "Mix the sanitizer by the label's measure until strips come.", sources: [maker("the sanitizer's label"), fc("4-302.14")] }, [fc("4-302.14")]),
  order("san.order", "Out of sanitizer: ordered more", "sanitizer", { text: "Sanitize with hot water at 171°F or above, or another sanitizer used as its label says.", sources: [fc("4-501.111"), fc("4-703.11")] }, [fc("4-501.114")]),
];
const HOT_WATER_SAN: FixOption[] = [
  { id: "hotsan.heated", label: "Heated the water to 171°F or above and checked again", sources: [fc("4-501.111")], outcome: "closes", who: "anyone" },
  repair("hotsan.repair", "Called for repair on the water heater", { text: "Sanitize with a chemical sanitizer at label strength until it's fixed.", sources: [fc("4-501.114"), fc("4-703.11(C)")] }),
];

/* ── supplies at a hand sink or toilet ── */

const SOAP: FixOption[] = [
  { id: "soap.refilled", label: "Refilled the hand soap", sources: [fc("6-301.11")], outcome: "closes", who: "anyone" },
  order("soap.order", "None left: ordered more soap", "hand-soap", { text: "Put a bottle of soap at this sink today, even a store-bought one.", sources: [fc("6-301.11"), advice()] }, [fc("6-301.11")]),
];
const TOWELS: FixOption[] = [
  { id: "towels.restocked", label: "Restocked the paper towels", sources: [fc("6-301.12")], outcome: "closes", who: "anyone" },
  order("towels.order", "None left: ordered more towels", "paper-towels", { text: "Use the hand dryer, or bring towels from another sink, until they come.", sources: [fc("6-301.12"), advice()] }, [fc("6-301.12")]),
  repair("towels.dryer", "Called for repair on the hand dryer", { text: "Put paper towels at the sink until it's fixed.", sources: [fc("6-301.12(A)")] }),
];
const TISSUE: FixOption[] = [
  { id: "tissue.restocked", label: "Restocked the toilet paper", sources: [fc("6-302.11")], outcome: "closes", who: "anyone" },
  order("tissue.order", "None left: ordered more", "toilet-paper", { text: "Bring toilet paper from home or a store today.", sources: [fc("6-302.11"), advice()] }, [fc("6-302.11")]),
];
const HAND_WATER = (where: string): FixOption[] => [
  { id: "handwater.ran", label: "Ran the tap and checked: 85°F or warmer", sources: [fc("5-202.12(A)")], outcome: "closes", who: "anyone" },
  repair("handwater.repair", "Called for repair", { text: `Wash hands at another hand sink with warm water until ${where} is fixed.`, sources: [fc("5-205.11(A)"), fc("5-202.12(A)")] }, [fc("5-205.15")]),
  { id: "handwater.stop", label: "Stopped food work and called the health department: no hot water anywhere", when: "No hot water at any hand sink", sources: [fc("8-404.11(A)"), fc("8-404.12")], outcome: "pending", who: "pic", whoWhy: STOP_WHO,
    pending: { kind: "repair", interim: { text: "No food work until the health department says you may open again.", sources: [fc("8-404.12")] } } },
];

/* ── the catalog, by rule id (templates.ts) ── */

const one = (id: string, label: string, sources: FixSource[], o: Partial<FixOption> = {}): FixOption => ({ id, label, sources, outcome: "closes", who: "anyone", ...o });

const DEFS: Record<string, { problem: string; options: FixOption[] }> = {
  cook_poultry: { problem: "Poultry under 165°F", options: COOK("165°F") },
  cook_ground: { problem: "Ground meat under 155°F", options: COOK("155°F for 17 seconds") },
  cook_whole: { problem: "Steak, chop or fish under 145°F", options: COOK("145°F") },
  cook_roast: { problem: "Roast under the chart", options: COOK("the roast chart's time and temperature") },
  reheat: { problem: "Reheat under 165°F", options: REHEAT },
  hot_hold: { problem: "Hot food below 135°F", options: HOT("135°F") },
  hot_hold_roast: { problem: "Roast below 130°F", options: HOT("130°F") },
  cold_hold: { problem: "Cold food above 41°F", options: FOOD_COLD },
  fridge: { problem: "Fridge above 41°F", options: FRIDGE },
  freezer: { problem: "Freezer above 0°F", options: FREEZER },
  cool_2h: { problem: "Not at 70°F by 2 hours", options: COOL_2H },
  cool_6h: { problem: "Not at 41°F by 6 hours", options: COOL_6H },
  chlorine: { problem: "Chlorine sanitizer off strength", options: SANITIZER },
  quat: { problem: "Quat sanitizer off strength", options: SANITIZER },
  iodine: { problem: "Iodine sanitizer off strength", options: SANITIZER },
  hot: { problem: "Sanitizing water under 171°F", options: HOT_WATER_SAN },
  recv_cold: { problem: "Delivery above 41°F", options: [
    one("recv.rejected", "Rejected it and sent it back", [fc("3-202.11(A)"), fc("2-103.11(E)")]),
    one("recv.discard", "Threw it out", [fc("3-701.11(A)")]),
    one("recv.law_temp", "It's milk or shellfish the law lets arrive warmer: cooled it to 41°F within 4 hours", [fc("3-202.11(B)"), fc("3-501.14(C)")], { when: "Milk or shellfish only", outcome: "recheck", recheckMin: 240 }),
  ] },
  cal: { problem: "Thermometer off by more than 2°F", options: [
    one("cal.adjusted", "Adjusted it to 32°F in ice water and tested again", [fc("4-502.11(B)"), maker("the thermometer's manual")]),
    one("cal.swapped", "Took it out of use and used another thermometer", [fc("4-502.11(B)"), fc("4-302.12(A)")]),
    order("cal.order", "Ordered a new thermometer", "thermometer", { text: "Use another accurate thermometer until it comes.", sources: [fc("4-302.12(A)"), fc("4-203.11")] }, [fc("4-302.12(A)")]),
  ] },
  tphc_marked: { problem: "Time-control food not marked", options: [
    one("tphc.discard", "Threw out the unmarked food", [fc("3-501.19(B)(5)")]),
  ] },
  rr_soap: { problem: "No hand soap", options: SOAP },
  rr_towels: { problem: "No paper towels or dryer", options: TOWELS },
  rr_tissue: { problem: "No toilet paper", options: TISSUE },
  rr_water: { problem: "No warm water at the sink", options: HAND_WATER("this sink") },
  rr_clean: { problem: "Restroom not clean", options: [one("rr.cleaned", "Cleaned and disinfected it", [fc("6-501.12(A)"), advice("restrooms cleaned and disinfected, source protocols")])] },
  rr_trash: { problem: "Restroom trash full", options: [one("rr.emptied", "Emptied the trash and put on the lid", [fc("5-501.113"), advice("source protocols")])] },
  rr_door: { problem: "Door doesn't close by itself", options: [
    one("rr.door_fixed", "Adjusted the door closer: it closes by itself now", [fc("6-202.14"), maker("the door closer's manual")]),
    repair("rr.door_repair", "Called for repair", { text: "Keep the door shut by hand. Never prop it open.", sources: [fc("6-202.14"), advice()] }),
  ] },
  task_done: { problem: "Task not done", options: [one("task.did", "Did it now", [advice("your checklist")])] },
  allergy_all_steps: { problem: "Allergy order: a safe step not done", options: [
    one("allergy.cant", "Told the server we can't make it safely", [fc("2-103.11(O)"), fc("3-304.15(A)")]),
    one("allergy.remade", "Remade it with every safe step", [fc("4-602.11(A)(5)"), fc("3-304.15(A)")]),
  ] },
  hands_no_bare: { problem: "Bare hand touched ready-to-eat food", options: [
    one("hands.redo", "Threw the food away, washed hands, put on new gloves", [fc("3-301.11(B)"), fc("3-701.11(D)"), fc("2-301.14")]),
  ] },
  recall_held: { problem: "Recalled food not held", options: [
    one("recall.held", "Pulled it, held it apart and labeled it 'Do not use'", [fc("6-404.11"), fc("3-302.11(A)(7)")]),
    one("recall.discard", "Threw it out as the notice says", [fc("3-701.11(A)")]),
  ] },
  storage_order_right: { problem: "Fridge out of order", options: [
    one("storage.moved", "Moved it: raw below ready-to-eat, raw poultry on the bottom", [fc("3-302.11(A)(1)-(2)")]),
  ] },
  dry_floor: { problem: "Food on the floor", options: [one("dry.lifted", "Lifted it 6 inches off the floor", [fc("3-305.11(A)(3)")])] },
  dry_drips: { problem: "Food under a drip", options: [
    one("dry.moved_drip", "Moved the food away from the drip", [fc("3-305.12")]),
    repair("dry.leak", "Called for repair on the leak", { text: "Keep food away from the drip until it's fixed.", sources: [fc("3-305.12")] }, [fc("5-205.15")]),
  ] },
  dry_room: { problem: "Food in a room it can't be in", options: [one("dry.moved_room", "Moved it to the kitchen or the storeroom", [fc("3-305.12(A)-(E)")])] },
  dry_covered: { problem: "Opened food not covered", options: [one("dry.covered", "Covered and labeled it, oldest in front", [fc("3-305.11(A)(2)"), advice("first in, first out")])] },
  dry_single: { problem: "Cups and lids not stored right", options: [one("dry.single", "Stored them covered, 6 inches up, away from drips", [fc("4-903.11(A), (C)")])] },
  transport_hot: { problem: "Hot food below 135°F on the road", options: HOT("135°F").filter((o) => o.id !== "hot.repair") },
  transport_roast: { problem: "Roast below 130°F on the road", options: HOT("130°F").filter((o) => o.id !== "hot.repair") },
  transport_cold: { problem: "Cold food above 41°F on the road", options: FOOD_COLD },
  returned_thrown_out: { problem: "Returned food not thrown out", options: [one("returned.discard", "Threw it out and logged it", [fc("3-306.14(A)"), fc("3-701.11")])] },
  water_hand: { problem: "Hand sink water under 85°F", options: HAND_WATER("the sink") },
  water_rinse_single: { problem: "Dish machine rinse out of range", options: [
    one("rinse.ok", "Ran the machine until the rinse was in range, and checked again", [fc("4-501.112(A)"), maker("the machine's data plate")]),
    repair("rinse.repair", "Called for repair on the dish machine", { text: "Wash, rinse and sanitize by hand with sanitizer at label strength until it's fixed.", sources: [fc("4-501.114"), fc("4-703.11(C)")] }),
  ] },
  water_rinse_other: { problem: "Dish machine rinse out of range", options: [
    one("rinse.ok", "Ran the machine until the rinse was in range, and checked again", [fc("4-501.112(A)"), maker("the machine's data plate")]),
    repair("rinse.repair", "Called for repair on the dish machine", { text: "Wash, rinse and sanitize by hand with sanitizer at label strength until it's fixed.", sources: [fc("4-501.114"), fc("4-703.11(C)")] }),
  ] },
  water_strip: { problem: "Dish machine sanitizer off", options: [
    one("strip.refilled", "Refilled the machine's sanitizer and tested again", [fc("4-501.114"), maker("the machine's data plate")]),
    repair("strip.repair", "Called for repair on the dish machine", { text: "Wash, rinse and sanitize by hand with sanitizer at label strength until it's fixed.", sources: [fc("4-501.114"), fc("4-703.11(C)")] }),
  ] },
  fire_ext: { problem: "Extinguisher tag out of date", options: [
    repair("fire.company", "Called the fire protection company", { text: "Keep a charged extinguisher in reach until it's serviced.", sources: [advice()] }, utRule(W.extinguisherRule)),
  ] },
  fire_hood: { problem: "Hood system tag out of date", options: [
    repair("fire.company", "Called the fire protection company", { text: "Ask the company if you may cook under the hood until it's serviced.", sources: [advice()] }, utRule(W.hoodRule)),
  ] },
  fire_alarm: { problem: "Alarm didn't beep", options: [
    one("fire.battery", "Changed the battery and tested it: it beeps", [advice("NFPA and USFA: test alarms monthly")]),
  ] },
  backflow_passed: { problem: "Backflow test didn't pass", options: [
    repair("backflow.call", "Called the plumber or the tester", { text: "Don't use the equipment until it passes.", sources: utRule(W.backflowRule) }, utRule(W.backflowRule)),
  ] },
  injury_food: { problem: "Food a cut may have touched", options: [
    one("injury.discard", "Threw away the food blood touched or might have, and cleaned and sanitized the tool and surface", [fc("3-701.11(D)"), advice("source protocols, cuts and burns")]),
  ] },
  injury_covered: { problem: "Cut or burn not covered", options: [
    one("injury.covered", "Covered it: waterproof bandage or finger cot, and a glove", [fc("2-401.13")]),
    one("injury.away", "Moved to jobs away from food", [fc("2-401.13"), advice()]),
  ] },
  prep_switch_done: { problem: "Raw to ready-to-eat switch not done", options: [
    one("switch.done", "Washed hands, cleaned and sanitized the board and tools, new gloves", [fc("4-602.11(A)"), fc("2-301.14(G)")]),
  ] },
  produce_wash_done: { problem: "Produce not washed", options: [one("wash.done", "Rinsed it under running water, scrubbed firm produce", [fc("3-302.15(A)")])] },
  advisory_done: { problem: "Menu reminder missing", options: [
    one("advisory.added", "Added the star and the reminder footnote", [fc("3-603.11(A)-(C)")]),
    one("advisory.off", "Took the dish off the menu until it's fixed", [fc("3-603.11"), advice()], { who: "pic", whoWhy: advice("the menu is the owner's or manager's call") }),
  ] },
  egg_done: { problem: "Pasteurized eggs not confirmed", options: [
    one("egg.switched", "Switched to pasteurized eggs", [fc("3-302.13")]),
    one("egg.off", "Stopped serving the raw-egg dish until they come", [fc("3-302.13")], { who: "pic", whoWhy: advice("the menu is the owner's or manager's call") }),
  ] },
  ice_scoop_out: { problem: "Scoop left in the ice", options: [one("ice.scoop", "Took the scoop out, washed and sanitized it, stored it handle up", [fc("3-304.12(E)")])] },
  ice_machine_clean: { problem: "Ice machine not cleaned", options: [
    one("ice.cleaned", "Emptied it and ran the maker's clean and sanitize cycle", [fc("4-602.11(E)(4)"), maker("the ice machine's manual")]),
    repair("ice.service", "Called for service", { text: "Use bagged ice from an approved supplier until it's cleaned.", sources: [fc("3-202.16"), fc("3-201.11(A)")] }),
  ] },
  ice_source_known: { problem: "Ice source not known", options: [one("ice.source", "Confirmed it: made here from drinking water, or bagged from an approved supplier", [fc("3-202.16"), fc("3-201.11(A)")])] },
  taste_once: { problem: "Tasting spoon used twice", options: [
    one("taste.discard", "Threw out the food the used spoon went back into", [fc("3-701.11(D)"), fc("3-301.12")]),
    one("taste.clean", "Used a clean spoon for each taste after that, and binned the used one", [fc("3-301.12")]),
  ] },
  sip_closed: { problem: "Drink where food is made", options: [one("sip.moved", "Moved the drink to the break area, or into a closed cup with a lid and a straw", [fc("2-401.11(A), (B)"), fc("6-403.11(A)")])] },
  break_area: { problem: "Break where food is made", options: [one("break.moved", "Took the break in the break area and washed hands after", [fc("2-401.11(A)"), fc("6-403.11(A)"), fc("2-301.14(D)")])] },
  utensil_place: { problem: "Utensil not in one of the six places", options: [one("utensil.reset", "Washed and sanitized it and rested it in one of the six places", [fc("3-304.12(A)-(F)"), fc("4-602.11(A)(5)")])] },
  single_right: { problem: "Cups, lids or straws not stored right", options: [one("single.stored", "Stored them covered and 6 inches up, and threw out any touched where lips go", [fc("4-903.11(A), (C)"), fc("4-904.11(A)")])] },
  ready_hair: { problem: "Hair not covered", options: [one("ready.hair", "Tied it back and put on a hat, hairnet or beard cover", [fc("2-402.11(A)")])] },
  ready_jewelry: { problem: "Rings or watch on", options: [one("ready.jewelry", "Took them off. A plain band can stay", [fc("2-303.11")])] },
  ready_nails: { problem: "Nails not ready", options: [one("ready.nails", "Washed and brushed under them. Polish or fake nails: gloves on", [fc("2-302.11(A), (B)")])] },
  ready_clothing: { problem: "Apron or clothes not clean", options: [one("ready.clothing", "Put on a clean apron or shirt", [fc("2-304.11")])] },
};

/** Utah home kitchens: the TCS item log's six readings (Utah Code 26B-7-416(8)(a)). */
for (const c of ["t1", "t2", "t3", "t4", "t5", "t6"]) DEFS[`tcs_${c}`] = { problem: "TCS item above 41°F", options: FOOD_COLD.map((o) => ({ ...o, sources: [...o.sources, ...utRule(W.mehkTcsRule)] })) };

/**
 * Rules with no "What I did": the health check-in's answers. A YES there is not a thing to fix; the person in charge's
 * exclude or restrict decision answers it (Food Code 2-201.12; checkin.ts), and it never leaves that private flow (T4).
 */
export const NOT_A_FIX: Record<string, string> = Object.fromEntries(
  ["hc_vomit_diarrhea", "hc_jaundice", "hc_sore_throat_fever", "hc_diagnosed_exposed", "hc_open_sore"].map((id) => [id, "The person in charge's check-in decision answers it (Food Code 2-201.12)."]),
);

export const CATALOG: Record<string, FindingFix> = Object.fromEntries(Object.entries(DEFS).map(([ruleId, d]) => [ruleId, { ruleId, problem: d.problem, options: d.options }]));

/** The quick buttons for one finding. Undefined for a rule with none (NOT_A_FIX, or a "missing" cell). */
export function whatIDidFor(ruleId: string): FindingFix | undefined {
  return CATALOG[ruleId];
}

/** The quick buttons for a row's violated rules, in rule order, one group per problem (duplicates folded). */
export function fixesForViolations(ruleIds: readonly string[]): FindingFix[] {
  const out: FindingFix[] = [];
  for (const id of ruleIds) { const f = CATALOG[id]; if (f && !out.some((x) => x.ruleId === id)) out.push(f); }
  return out;
}

/* ─────────────── how long over the limit (W8) ─────────────── */

/**
 * Food above 41°F or below 135°F: how long has it been over the limit? The answer picks the options (owner and UX review
 * 2026-10-01, must-fix A6: "the novice gets the decision, not a blank box").
 *   - 4 hours or more, or nobody knows: throw it out. Food Code 3-501.19(B)(4): food out of temperature control is served or
 *     "discarded, within 4 hours"; (B)(5): food not marked with its time "shall be discarded". No provision of the Code keeps
 *     TCS food out of temperature control past 4 hours (the 6-hour cold option, 3-501.19(C), needs written steps, a start at
 *     41°F or below and the food never above 70°F), so 4 hours is the outside limit.
 *   - 2 to 4 hours: throw it out (our advice, the parish protocol), or, only with a written time-control plan, use it or throw it
 *     out by the 4-hour mark (3-501.19(A)-(B); the person in charge).
 *   - Under 2 hours: chill it back to 41°F or below, or reheat it to 165°F within 2 hours (our advice, the parish protocol;
 *     3-501.16(A), 3-403.11(A), (D)).
 * A finding fixed later: if the food is still where it was read, the time since the reading counts, so a band below it can't be
 * picked (`bandFloor`).
 */
export type OverBand = "under2" | "2to4" | "over4" | "unknown";
export const OVER_BANDS: readonly { id: OverBand; label: string }[] = [
  { id: "under2", label: "Under 2 hours" }, { id: "2to4", label: "2 to 4 hours" }, { id: "over4", label: "4 hours or more" }, { id: "unknown", label: "I don't know" },
];
export const OVER_QUESTION = "How long has the food been over the limit?";
/** The plain rule, said once under the question, with its source (W1). */
export const FOUR_HOUR_RULE: Interim = {
  text: "Above 41°F or below 135°F for 4 hours or more, or for a time nobody knows: throw it out. Our advice: at 2 hours, throw it out unless your written time plan covers it.",
  sources: [fc("3-501.19(B)(4)-(5)"), fc("3-501.16(A)"), advice("the 2-hour line, parish protocol, corrective actions at CCPs")],
};
/** Rules where the food's own temperature is over the limit: the question is asked. */
export const TIMED_RULES: ReadonlySet<string> = new Set(["fridge", "cold_hold", "transport_cold", "hot_hold", "hot_hold_roast", "transport_hot", "transport_roast", "tcs_t1", "tcs_t2", "tcs_t3", "tcs_t4", "tcs_t5", "tcs_t6"]);
const BAND_RANK: Record<OverBand, number> = { under2: 0, "2to4": 1, over4: 2, unknown: 3 };

/** The least it can be, when the food is still where it was read: the time since the reading. */
export function bandFloor(readingAt: IsoDateTime, now: IsoDateTime): Exclude<OverBand, "unknown"> {
  const min = (Date.parse(now) - Date.parse(readingAt)) / 60_000;
  return min >= 240 ? "over4" : min >= 120 ? "2to4" : "under2";
}
/** The answers a person may give: at or above the floor, and "I don't know". */
export function bandsFrom(floor?: OverBand): OverBand[] {
  return OVER_BANDS.map((b) => b.id).filter((b) => b === "unknown" || !floor || BAND_RANK[b] >= BAND_RANK[floor]);
}
/** The options that fit an answer. Without an answer: only the options that don't depend on it. */
export function optionsForBand(f: FindingFix, band?: OverBand): FixOption[] {
  return f.options.filter((o) => !o.bands || (band !== undefined && o.bands.includes(band)));
}
/** Does this finding ask how long? */
export const asksBand = (f: FindingFix) => TIMED_RULES.has(f.ruleId) && f.options.some((o) => o.bands);

/* ─────────────── who may pick ─────────────── */

export interface FixActor { id: Id; name: string; isPic: boolean }

/** The person in charge on this shift, a manager, an admin or the owner. */
export function actorIsPic(o: { role?: string; isPersonInCharge?: boolean }): boolean {
  return !!o.isPersonInCharge || o.role === "owner" || o.role === "admin" || o.role === "manager";
}

/** What a person sees on the button: the option, or "Hand this to Maria" when only the person in charge may (W3). */
export function optionFor(o: FixOption, isPic: boolean, picName?: string): { mode: "do" | "handoff"; text: string } {
  if (o.who === "anyone" || isPic) return { mode: "do", text: o.label };
  return { mode: "handoff", text: picName ? `Hand this to ${picName}` : "Hand this to the person in charge" };
}

/** What to do while a hand-off waits: the pending control when it has one, else keep the food out of use (W3). */
export function holdWhileHanded(o: FixOption): Interim {
  if (o.pending) return o.pending.interim;
  return { text: "Keep the food out of use, labeled \"Do not use\", until they decide.", sources: [fc("3-701.11(A)"), advice()] };
}

/* ─────────────── the record ─────────────── */

/**
 * Where a finding stands (W2). `open`: nothing done yet, or the record says only what was asked ("Asked to: … Not done yet."
 * from the one-tap cards). `followed_up`: a recheck came back still out of range; the newer reading is its own finding.
 */
export type FixStatus = "open" | "fixed" | "recheck" | "waiting" | "handed" | "followed_up";

export interface FixStep {
  /** Catalog option, absent for "Something else". */
  optionId?: string;
  ruleId?: string;
  label: string;
  /** Source lines as written at the time ("Food Code 3-501.16(A)(2)"). */
  sources: string[];
  kind: "did" | "handoff" | "arrived" | "recheck" | "asked";
  outcome?: FixOutcome;
  by: Id;
  byName: string;
  at: IsoDateTime;
  /** handoff: whose list it went to. */
  toName?: string;
  /** How long the food was over its limit, as the person answered (W8). */
  band?: OverBand;
  /** recheck: the reading that answered it, and whether it was in range. */
  entryId?: Id;
  ok?: boolean;
}

export interface PendingFix {
  kind: "repair" | "supply";
  /** What is broken or out: "walk-in fridge", "hand soap". */
  what: string;
  supplyKey?: string;
  interim: string;
  interimSources: string[];
  since: IsoDateTime;
  arrivedAt?: IsoDateTime;
  arrivedBy?: string;
}

export interface FixRecord {
  status: FixStatus;
  /** The thing the finding is about: "walk-in fridge", "chicken", "men's room". */
  what: string;
  steps: FixStep[];
  /** recheck: when the recheck reading is due, and the cells a recheck must match (the same unit, the same food). */
  recheckAt?: IsoDateTime;
  recheckMatch?: Record<string, string>;
  pending?: PendingFix;
  /** handed: whose list it is on. */
  handedTo?: string;
  /** The violated rules this record answers. */
  ruleIds: string[];
}

/** Columns that name the thing a reading is about, per template (the recheck must match them). */
const IDENTITY: Record<string, string[]> = {
  cold_units: ["unit", "kind"], temp_cook_hold: ["food", "stage"], tcs_items: ["item"], sanitizer: ["where", "kind"], water_temp: ["point", "machine"],
  restroom_check: ["room"], calibration: ["thermometer"], transport_temp: ["food", "kind"], fire_check: ["item"], receiving: ["item"], cooling: ["food"],
  dry_storage: ["where"], storage_order: ["where"], ice_check: ["check"],
};

export function identityOf(templateId: string, values: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of IDENTITY[templateId] ?? []) { const v = values[k]; if (typeof v === "string" && v.trim()) out[k] = v; }
  return out;
}

/** "walk-in fridge": the first named thing on the row, else a short name for the check. */
export function whatOf(e: { templateId: string; values: Record<string, unknown> }, title?: string): string {
  const id = identityOf(e.templateId, e.values);
  const first = Object.values(id)[0];
  if (first) return first;
  return (title ?? e.templateId).split(/[:,(]/)[0]!.trim().toLowerCase().replace(/ log$/, "");
}

/** The record's one-line summary for the paper's action column (W6: what was done, by whom). */
const ASKED_ONLY = /\bNot (done|fixed|moved|confirmed) yet\.\s*$/;

/** Where a row stands, from its record (W2). Rows without a record: fixed when it has an action, unless the action only says what was asked. */
export function fixStatusOf(e: { evaluation: { ok: boolean }; correctiveAction?: string; fix?: FixRecord }): FixStatus | "ok" {
  if (e.fix) return e.fix.status;
  if (e.evaluation.ok) return "ok";
  const a = e.correctiveAction?.trim();
  if (!a || ASKED_ONLY.test(a)) return "open";
  return "fixed";
}

/** The words for a status (binder, Prove, Today's records). */
export function fixStatusLabel(e: { evaluation: { ok: boolean }; correctiveAction?: string; fix?: FixRecord }): string {
  const s = fixStatusOf(e);
  switch (s) {
    case "ok": return "OK";
    case "open": return "Needs a fix";
    case "fixed": return "Fixed";
    case "recheck": return "Recheck due";
    case "waiting": return e.fix?.pending?.arrivedAt ? "Recheck due" : e.fix?.pending?.kind === "supply" ? "Waiting on the order" : "Waiting on the repair";
    case "handed": return e.fix?.handedTo ? `Handed to ${e.fix.handedTo}` : "Handed to the person in charge";
    case "followed_up": return "Rechecked: still out";
  }
}

/** A tone for a status: fixed is a win, waiting and handed are on someone's list, open needs a fix. */
export function fixStatusTone(e: Parameters<typeof fixStatusLabel>[0]): "ok" | "warn" | "bad" | undefined {
  const s = fixStatusOf(e);
  return s === "ok" ? undefined : s === "open" ? "bad" : "warn";
}

/** Closed: nothing more to do on this finding. */
export const isClosed = (s: FixStatus | "ok") => s === "ok" || s === "fixed" || s === "followed_up";

export interface WhatIDidInput {
  entry: { templateId: string; values: Record<string, unknown>; evaluation: { violations: { ruleId: string }[] }; correctiveAction?: string; fix?: FixRecord; createdAt?: IsoDateTime };
  picks: string[];
  /** "Something else": a person's own words (short). */
  other?: string;
  actor: FixActor;
  /** Whose list a hand-off lands on ("Maria"). */
  picName?: string;
  now: IsoDateTime;
  /** Template title, for `what` when the row names nothing. */
  title?: string;
  /** W8: how long the food was over its limit; and whether it is still where it was read (then the time since counts). */
  band?: OverBand;
  stillThere?: boolean;
}

export interface WhatIDidResult {
  fix: FixRecord;
  correctiveAction: string;
  /** A hand-off to file with the person in charge (a report from the record; W7: no health words). */
  handoff?: { details: string; options: FixOption[] };
  /** A pending fix was started: tell the managers (by their reach preferences). */
  notifyManagers?: true;
}

const rulesOf = (e: WhatIDidInput["entry"]) => e.evaluation.violations.map((v) => v.ruleId).filter((id) => CATALOG[id]);
const addMinutes = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();

/** The options a row offers, keyed by option id (an id shared by two problems is the same act). */
export function optionsForEntry(e: WhatIDidInput["entry"]): Map<string, { option: FixOption; ruleId: string }> {
  const out = new Map<string, { option: FixOption; ruleId: string }>();
  for (const f of fixesForViolations(rulesOf(e))) for (const o of f.options) if (!out.has(o.id)) out.set(o.id, { option: o, ruleId: f.ruleId });
  return out;
}

/** "What I did: Moved the food …; Called for repair (waiting). Handed to Maria: Ordered the part." */
export function composeNote(steps: readonly FixStep[]): string {
  const did = steps.filter((s) => s.kind === "did").map((s) => (s.outcome === "pending" ? `${s.label} (waiting)` : s.label));
  const handed = steps.filter((s) => s.kind === "handoff");
  const parts: string[] = [];
  const band = steps.find((s) => s.band)?.band;
  const over = band ? ` (over the limit ${band === "unknown" ? "for a time nobody knows" : OVER_BANDS.find((b) => b.id === band)!.label.toLowerCase()})` : "";
  if (did.length) parts.push(`What I did${over}: ${did.join("; ")}.`);
  if (handed.length) parts.push(`Handed to ${handed[0]!.toName ?? "the person in charge"}: ${handed.map((s) => s.label).join("; ")}.`);
  return parts.join(" ");
}

/**
 * Record what a person did (W1–W3). Errors: no pick, an unknown pick, words too long. A person-in-charge option picked by
 * someone who isn't one is handed off, never recorded as done.
 */
export function applyWhatIDid(i: WhatIDidInput): WhatIDidResult | { error: string } {
  const offered = optionsForEntry(i.entry);
  const other = i.other?.trim() ?? "";
  if (other.length > OTHER_MAX) return { error: `Keep "Something else" under ${OTHER_MAX} letters.` };
  const picks = [...new Set(i.picks)];
  if (!picks.length && !other) return { error: "Tap what you did, or Something else." };
  for (const p of picks) if (!offered.has(p)) return { error: "That choice isn't one for this problem." };
  // W8: a choice that depends on how long the food was over its limit needs the answer, and the answer must fit it.
  const banded = picks.map((p) => offered.get(p)!.option).filter((o) => o.bands);
  if (i.band && !OVER_BANDS.some((b) => b.id === i.band)) return { error: "Pick how long the food was over the limit." };
  if (banded.length && !i.band) return { error: "Say how long the food was over the limit first." };
  if (i.band && i.stillThere && i.entry.createdAt && !bandsFrom(bandFloor(i.entry.createdAt, i.now)).includes(i.band)) return { error: "The food has been there since the reading, so it's been over the limit at least that long." };
  if (i.band) for (const o of banded) if (!o.bands!.includes(i.band)) return { error: `"${o.label}" doesn't fit ${OVER_BANDS.find((b) => b.id === i.band)!.label.toLowerCase()} over the limit.` };
  const base = { by: i.actor.id, byName: i.actor.name, at: i.now };
  const steps: FixStep[] = [];
  const handed: FixOption[] = [];
  for (const p of picks) {
    const { option: o, ruleId } = offered.get(p)!;
    if (o.who === "pic" && !i.actor.isPic) { handed.push(o); steps.push({ ...base, optionId: o.id, ruleId, label: o.label, sources: o.sources.map((s) => s.cite), kind: "handoff", toName: i.picName }); continue; }
    steps.push({ ...base, optionId: o.id, ruleId, label: o.label, sources: o.sources.map((s) => s.cite), kind: "did", outcome: o.outcome, ...(i.band && TIMED_RULES.has(ruleId) ? { band: i.band } : {}) });
  }
  if (other) steps.push({ ...base, label: other, sources: [], kind: "did", outcome: "closes" });
  const done = steps.filter((s) => s.kind === "did");
  const doneOpts = done.map((s) => (s.optionId ? offered.get(s.optionId)!.option : undefined)).filter((o): o is FixOption => !!o);
  const pending = doneOpts.find((o) => o.pending);
  const rechecks = doneOpts.filter((o) => o.outcome === "recheck");
  const ruleIds = rulesOf(i.entry);
  const what = whatOf(i.entry, i.title);
  const prior = i.entry.fix?.steps ?? (i.entry.correctiveAction?.trim() ? [{ ...base, label: i.entry.correctiveAction.trim(), sources: [], kind: "asked" as const }] : []);
  const status: FixStatus = handed.length ? "handed" : pending ? "waiting" : rechecks.length ? "recheck" : "fixed";
  const fix: FixRecord = { status, what, steps: [...prior, ...steps], ruleIds };
  if (handed.length) fix.handedTo = i.picName ?? "the person in charge";
  if (pending) {
    const pk = pending.pending!;
    const supplyName = pk.supplyKey?.replace(/-/g, " ");
    fix.pending = { kind: pk.kind, what: pk.kind === "supply" && supplyName ? supplyName : what, ...(pk.supplyKey ? { supplyKey: pk.supplyKey } : {}), interim: pk.interim.text, interimSources: pk.interim.sources.map((s) => s.cite), since: i.now };
  }
  if (!handed.length && !pending && rechecks.length) {
    fix.recheckAt = addMinutes(i.now, Math.max(...rechecks.map((o) => o.recheckMin ?? 30)));
    fix.recheckMatch = identityOf(i.entry.templateId, i.entry.values);
  }
  const res: WhatIDidResult = { fix, correctiveAction: composeNote(steps) };
  if (handed.length) res.handoff = { details: handoffDetails(fix, handed), options: handed };
  if (pending && i.actor.isPic) res.notifyManagers = true;
  return res;
}

/** The full note after a new round: what was there before, then what was just done (a history, oldest first). */
export const appendNote = (prev: string | undefined, add: string) => [prev?.trim(), add.trim()].filter(Boolean).join(" ");

/**
 * The row after a fix is recorded: the note in the paper's action column too, evaluated again (the API and both demos
 * write the same row). The reading itself never changes: a fix is added to a record, never edits what was read.
 */
export function withFix(e: { templateId: string; values: Record<string, CellValue>; correctiveAction?: string }, fix: FixRecord, note: string): { values: Record<string, CellValue>; correctiveAction: string; evaluation: Evaluation; fix: FixRecord } {
  const t = getTemplate(e.templateId);
  const col = t?.rules.find((r) => r.op === "required_if_violation")?.column;
  const values = t ? normalizeRow(t, { ...e.values, ...(col ? { [col]: note } : {}) }) : { ...e.values };
  return { values, correctiveAction: note, evaluation: t ? evaluateRow(t, values) : { ok: false, violations: [] }, fix };
}

/** What a hand-off report says: the problem, the thing, what needs the person in charge (W7: never anything about health). */
export const handoffDetails = (fix: Pick<FixRecord, "ruleIds" | "what">, options: readonly FixOption[]) =>
  `${CATALOG[fix.ruleIds[0] ?? ""]?.problem ?? "A problem"} (${fix.what}): ${options.map((o) => o.label).join("; ")}. Needs the person in charge.`;

/** "It's fixed" / "It came": the pending fix arrived; the recheck reading is due now (W5). */
export function arriveFix(e: { templateId: string; values: Record<string, unknown>; fix?: FixRecord }, actor: FixActor, now: IsoDateTime): FixRecord | { error: string } {
  const f = e.fix;
  if (!f?.pending || f.status !== "waiting") return { error: "Nothing is waiting on a repair or an order here." };
  const label = f.pending.kind === "repair" ? `The repair is done: ${f.pending.what}` : `It came: ${f.pending.what}`;
  return {
    ...f, status: "recheck", recheckAt: now, recheckMatch: identityOf(e.templateId, e.values),
    pending: { ...f.pending, arrivedAt: now, arrivedBy: actor.name },
    steps: [...f.steps, { label, sources: [], kind: "arrived", by: actor.id, byName: actor.name, at: now }],
  };
}

/** The note after an arrival: the earlier note plus what happened. */
export const arrivalNote = (prev: string | undefined, f: FixRecord) => [prev?.trim(), `${f.steps[f.steps.length - 1]!.label}. Recheck due.`].filter(Boolean).join(" ");

type Row = { id: Id; version?: number; locationId?: Id; templateId: string; values: Record<string, unknown>; evaluation: { ok: boolean; violations: { ruleId: string }[] }; createdAt: IsoDateTime; correctiveAction?: string; fix?: FixRecord; correction?: unknown };

/**
 * A new reading answers open rechecks (W4, W5): same place, same template, same unit or food, made after the record's last
 * step. In range on the rules the record answers → fixed; still out → followed up (the new reading is its own finding).
 */
export function closeRechecks(open: readonly Row[], reading: Row, by: FixActor): { id: Id; fix: FixRecord; correctiveAction: string }[] {
  if (reading.correction) return [];
  const out: { id: Id; fix: FixRecord; correctiveAction: string }[] = [];
  for (const r of open) {
    const f = r.fix;
    if (!f || f.status !== "recheck" || r.id === reading.id || r.templateId !== reading.templateId) continue;
    if (r.locationId && reading.locationId && r.locationId !== reading.locationId) continue;
    const last = f.steps[f.steps.length - 1]?.at ?? r.createdAt;
    if (reading.createdAt <= last) continue;
    if (!Object.entries(f.recheckMatch ?? {}).every(([k, v]) => String(reading.values[k] ?? "").toLowerCase() === v.toLowerCase())) continue;
    const ok = !reading.evaluation.violations.some((v) => f.ruleIds.includes(v.ruleId));
    const step: FixStep = { label: ok ? "Rechecked: in range" : "Rechecked: still out of range", sources: [], kind: "recheck", entryId: reading.id, ok, by: by.id, byName: by.name, at: reading.createdAt };
    const fix: FixRecord = { ...f, status: ok ? "fixed" : "followed_up", steps: [...f.steps, step] };
    delete fix.recheckAt;
    out.push({ id: r.id, fix, correctiveAction: [r.correctiveAction?.trim(), `${step.label}.`].filter(Boolean).join(" ") });
  }
  return out;
}

/* ─────────────── open fixes: Today and the order list ─────────────── */

export interface OpenFixView {
  entryId: Id;
  /** The record's version, for "It's fixed" (POST …/fix-arrived). */
  version?: number;
  status: Extract<FixStatus, "waiting" | "handed" | "recheck">;
  /** "Waiting on the repair: walk-in fridge". */
  title: string;
  what: string;
  /** What to do meanwhile, and its source (W1). */
  interim?: { text: string; source: string };
  since: IsoDateTime;
  /** The one action: "It's fixed" (repair), "It came" (supply), "Take the recheck" (recheck). */
  action: { kind: "arrived" | "recheck"; label: string };
  supplyKey?: string;
  /** A supply that the order list says arrived after the fix started: tap to restock and recheck. */
  arrived?: true;
  templateId: string;
  recheckAt?: IsoDateTime;
  recheckKey?: string;
}

/** The expected-check key of a record's recheck (schedule.ts). */
export const recheckKey = (entryId: Id) => `recheck:${entryId}`;

/**
 * Every open fix at a place, newest first: waiting on a repair or an order, handed to the person in charge, or a recheck due.
 * `arrivals`: supply keys with the time the order list last marked them arrived (a supply that came shows "It came").
 */
export function openFixViews(rows: readonly Row[], arrivals: Record<string, IsoDateTime | undefined> = {}): OpenFixView[] {
  const out: OpenFixView[] = [];
  for (const r of rows) {
    const f = r.fix;
    if (!f || r.correction || (f.status !== "waiting" && f.status !== "handed" && f.status !== "recheck")) continue;
    const since = f.pending?.since ?? f.steps[f.steps.length - 1]?.at ?? r.createdAt;
    if (f.status === "waiting" && f.pending) {
      const p = f.pending;
      const came = p.kind === "supply" && p.supplyKey && arrivals[p.supplyKey] && arrivals[p.supplyKey]! > p.since;
      out.push({
        entryId: r.id, ...(r.version ? { version: r.version } : {}), status: "waiting", what: p.what, templateId: r.templateId, since,
        title: p.kind === "repair" ? `Waiting on the repair: ${p.what}` : came ? `It came: ${p.what}` : `Waiting on the order: ${p.what}`,
        interim: { text: p.interim, source: p.interimSources.map((s) => (s.startsWith("Our advice") ? "Our advice" : s)).join(" · ") },
        action: { kind: "arrived", label: p.kind === "repair" ? "It's fixed" : came ? "Restocked: recheck now" : "It came" },
        ...(p.supplyKey ? { supplyKey: p.supplyKey } : {}), ...(came ? { arrived: true as const } : {}),
      });
    } else if (f.status === "handed") {
      const last = [...f.steps].reverse().find((s) => s.kind === "handoff");
      out.push({ entryId: r.id, ...(r.version ? { version: r.version } : {}), status: "handed", what: f.what, templateId: r.templateId, since, title: `Handed to ${f.handedTo ?? "the person in charge"}: ${f.what}`, ...(last ? { interim: { text: last.label, source: last.sources.join(" · ") } } : {}), action: { kind: "recheck", label: "Pick what was done" } });
    } else {
      out.push({ entryId: r.id, ...(r.version ? { version: r.version } : {}), status: "recheck", what: f.what, templateId: r.templateId, since, title: `Recheck due: ${f.what}`, action: { kind: "recheck", label: "Take the recheck" }, ...(f.recheckAt ? { recheckAt: f.recheckAt } : {}), recheckKey: recheckKey(r.id) });
    }
  }
  return out.sort((a, b) => b.since.localeCompare(a.since));
}

/** Supplies an open fix waits on, for the order list (W5): ordered, or handed to the person in charge to order, and not yet arrived. */
export function pendingSupplyKeys(rows: readonly Row[], arrivals: Record<string, IsoDateTime | undefined> = {}): string[] {
  const keys = new Set<string>();
  for (const r of rows) {
    const f = r.fix;
    if (!f || r.correction) continue;
    if (f.status === "waiting" && f.pending?.supplyKey && !f.pending.arrivedAt && !(arrivals[f.pending.supplyKey] && arrivals[f.pending.supplyKey]! > f.pending.since)) keys.add(f.pending.supplyKey);
    if (f.status === "handed") for (const s of f.steps) if (s.kind === "handoff" && s.optionId) { const o = CATALOG[s.ruleId ?? ""]?.options.find((x) => x.id === s.optionId); if (o?.pending?.supplyKey) keys.add(o.pending.supplyKey); }
  }
  return [...keys];
}

/** The newest arrival time per supply key, from order records (ordering.ts SupplierOrder lines). */
export function arrivalsOf(orders: readonly { lines: readonly { key: string; arrivedAt?: IsoDateTime }[]; correction?: unknown }[]): Record<string, IsoDateTime> {
  const out: Record<string, IsoDateTime> = {};
  for (const o of orders) if (!o.correction) for (const l of o.lines) if (l.arrivedAt && (!out[l.key] || l.arrivedAt > out[l.key]!)) out[l.key] = l.arrivedAt;
  return out;
}

/* ─────────────── who's carrying it (delegate.ts, migration 0062) ─────────────── */

/**
 * An open fix is an item someone carries (W9): a hand-off is assigned to the person in charge it went to (via "handoff"); a
 * fix waiting on a repair or an order is assigned to the person in charge who recorded it, who follows it up. The item key
 * is "fix:<entryId>", scope "standing" (a repair can take days), so "Who's carrying what" lists it until it closes. A recheck
 * is assigned to whoever tapped "It's fixed" (scope today, its own key "recheck:<entryId>"). Every event is append-only.
 */
export const fixItemKey = (entryId: Id) => `fix:${entryId}`;

export function fixAssignment(o: { itemKey: string; title: string; to: { employeeId: Id; name: string }; by: { employeeId?: Id; name: string }; via: "handoff" | "assign"; at: IsoDateTime; date?: string }): Omit<TaskAssignment, "id" | "orgId" | "locationId"> {
  return {
    itemKey: o.itemKey, itemTitle: o.title.slice(0, 200), action: "assign", scope: o.date ? "today" : "standing", ...(o.date ? { date: o.date } : {}),
    targetKind: "person", employeeId: o.to.employeeId, ...(o.by.employeeId ? { byEmployeeId: o.by.employeeId } : {}), byName: o.by.name, via: o.via, at: o.at,
  };
}

/** A hand-off waits on the person in charge; after an hour it "needs a hand" on the carrying list (our advice). */
export const HANDOFF_CARRY_MIN = 60;
/**
 * Open fixes as rows of "Who's carrying what" (delegate.ts carryingView reads expected checks): handed and waiting ones. A
 * recheck is already an expected check. Waiting on a repair or an order is never "late": it is waiting.
 */
export function fixCarryRows(views: readonly OpenFixView[]): (import("./schedule.ts").ExpectedCheck & { satisfied: false })[] {
  return views.filter((v) => v.status === "handed" || v.status === "waiting").map((v) => ({
    key: fixItemKey(v.entryId), label: v.title, templateId: v.templateId, blocking: false, required: false, requirementIds: [], dueAt: v.since,
    lateAt: new Date(Date.parse(v.since) + (v.status === "handed" ? HANDOFF_CARRY_MIN : 365 * 1440) * 60_000).toISOString(), satisfied: false as const,
  }));
}

/**
 * Supplies go through the order-list alert (delegate's orderalert.ts, "Thank Susan"), never a second message (W10): a supply
 * handed to the person in charge, or ordered by them, is flagged "out" once; only a repair (or a decision about food) is a
 * report from the record. Returns the supply keys to flag and the hand-off options still to file as a report.
 */
export function splitHandoff(options: readonly FixOption[]): { supplies: string[]; report: FixOption[] } {
  const supplies = [...new Set(options.filter((o) => o.pending?.kind === "supply" && o.pending.supplyKey).map((o) => o.pending!.supplyKey!))];
  return { supplies, report: options.filter((o) => !(o.pending?.kind === "supply" && o.pending.supplyKey)) };
}

/** Words that blame a person (W6); none of the catalog's words may use them. */
export const FIX_BLAME_WORDS = /\b(fail(ed|ure)?|fault|forgot|careless|lazy|negligen\w*|blame\w*|mistake|wrong(ly)?|should have|didn't bother|violation)\b/i;
